import React from 'react';
import { Portal } from './Portal';
import { X } from 'lucide-react';

interface LightboxProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  alt?: string;
  title?: string;
}

export const Lightbox: React.FC<LightboxProps> = ({ 
  isOpen, 
  onClose, 
  imageSrc, 
  alt = 'Enlarged image',
  title
}) => {
  // Prevent body scroll when lightbox is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle keyboard navigation for accessibility
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isOpen && e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <Portal>
      <div 
        className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lightbox-title"
      >
        <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors z-10"
            aria-label="Close lightbox"
          >
            <X className="w-6 h-6" />
          </button>
          <div 
            className="flex items-center justify-center"
            itemScope
            itemType="http://schema.org/ImageObject"
          >
            <img
              src={imageSrc}
              alt={alt}
              title={title || alt}
              itemProp="contentUrl"
              className="max-h-[85vh] max-w-full object-contain bg-transparent rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              loading="eager"
              decoding="sync"
            />
            {/* Hidden structured data for SEO */}
            <meta itemProp="name" content={title || alt} />
            <meta itemProp="description" content={alt} />
            <span id="lightbox-title" className="sr-only">{title || alt}</span>
          </div>
        </div>
      </div>
    </Portal>
  );
}; 